import React, { useState } from 'react';
import { X, Search, Loader } from 'lucide-react';

const PATTERN_OPTIONS = [
  { id: 'ssn', label: 'Social Security Numbers', description: 'XXX-XX-XXXX format' },
  { id: 'phone', label: 'Phone Numbers', description: 'US phone number formats' },
  { id: 'email', label: 'Email Addresses', description: 'name@domain.com format' },
  { id: 'dob', label: 'Dates of Birth', description: 'DOB: MM/DD/YYYY format' },
  { id: 'credit_card', label: 'Credit Card Numbers', description: 'XXXX-XXXX-XXXX-XXXX format' },
];

const RedactionAutoDetectModal = ({ isOpen, onClose, onDetect, onAddMatches, loading }) => {
  const [selectedPatterns, setSelectedPatterns] = useState(['ssn', 'phone', 'email']);
  const [matches, setMatches] = useState(null);

  const togglePattern = (id) => {
    setSelectedPatterns((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleDetect = async () => {
    if (selectedPatterns.length === 0) return;
    const result = await onDetect(selectedPatterns);
    if (result) {
      setMatches(result);
    }
  };

  const handleAddAll = () => {
    if (matches && matches.matches.length > 0) {
      onAddMatches(matches.matches);
      setMatches(null);
      onClose();
    }
  };

  const handleClose = () => {
    setMatches(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Auto-Detect Sensitive Information</h3>
          <button onClick={handleClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {!matches ? (
            <>
              <p className="text-sm text-gray-600">
                Select the types of sensitive information to automatically detect:
              </p>
              <div className="space-y-2">
                {PATTERN_OPTIONS.map((pattern) => (
                  <label
                    key={pattern.id}
                    className="flex items-start gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPatterns.includes(pattern.id)}
                      onChange={() => togglePattern(pattern.id)}
                      className="mt-0.5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{pattern.label}</div>
                      <div className="text-xs text-gray-500">{pattern.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </>
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-3">
                Found <span className="font-semibold">{matches.total_count}</span> potential match{matches.total_count !== 1 ? 'es' : ''}:
              </p>
              {matches.total_count > 0 ? (
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {matches.matches.map((match, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                      <span className="font-mono text-xs truncate flex-1">{match.original_text}</span>
                      <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                        p.{match.page_number} - {match.mark_type.replace('auto_', '')}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No matches found for the selected patterns.</p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={handleClose}
            className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded"
          >
            Cancel
          </button>
          {!matches ? (
            <button
              onClick={handleDetect}
              disabled={loading || selectedPatterns.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50"
            >
              {loading ? <Loader className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Detect
            </button>
          ) : matches.total_count > 0 ? (
            <button
              onClick={handleAddAll}
              className="px-3 py-1.5 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600"
            >
              Add All {matches.total_count} Marks
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default RedactionAutoDetectModal;
